import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Form, Button, Container, Spinner, InputGroup } from 'react-bootstrap';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { DataContext } from '../../adapter';
import { useNavigate } from 'react-router-dom';
import { ClaimCreateService } from '../../domain';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { modalSuccessClaimsCreateComponent, modalOptToUploadComponent } from '../components';
import Select from 'react-select';
import { NumbersOnlyUtil } from '../../utils';

const { sourceOfFunds, employerBusinessNature } = require('../../services/data/mapping');

const MySwal = withReactContent(Swal);

const StyledContainer = styled(Container)`
    font-family: 'Montserrat', sans-serif;
    color: rgba(41, 37, 97, 1);
    background: rgb(234, 222, 208, 1);
    padding: 20px 20%;

    @media (min-width: 300px) and (max-width: 500px) {
        width: 100%;
        padding: 2% 10%;
    }
`;

const StyledContainer2 = styled(Container)`
    padding: 0 5%;

    @media (min-width: 300px) and (max-width: 500px) {
        width: 100%;
        padding: 0;
    }
`;

const Title = styled.h1`
    font-size: 42px;
    font-weight: 700;
    text-align: left;
`;

const Description = styled.p`
    font-size: 22px;
    font-weight: 500;
`;

const StyledDescription = styled.p`
    font-style: italic;
    font-size: 14px;
    color: black;
    display: none;
    position: absolute;
    background: rgba(255, 255, 255, 1);
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    width: 520px;
    z-index: 100;
    top: -60px;
    left: -220px;

    @media (min-width: 300px) and (max-width: 500px) {
        width: 300px;
        left: -150px;
        top: -95px;
    }
`;

const StyledForm = styled(Form)`
    .form-group-label {
        font-size: 16px;
        font-weight: 500;
    }
`;

const StyledSubmitButton = styled(Button)`
    background-color: rgba(176, 17, 22, 1);
    max-width: fit-content;
    padding: 5px 40px;
    border-radius: 25px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 20px;
    color: white;
    text-align: center;

    @media (min-width: 300px) and (max-width: 500px) {
        font-size: 16px;
        padding: 5px 20px;
        text-align: center;
    }
`;

const StyledSubmitButton2 = styled(Button)`
    background-color: rgba(176, 17, 22, 1);
    max-width: fit-content;
    padding: 5px 65px;
    border-radius: 25px;
    font-family: 'Montserrat', sans-serif;
    font-weight: 700;
    font-size: 20px;
    color: white;
    text-align: center;

    @media (min-width: 300px) and (max-width: 500px) {
        font-size: 16px;
        padding: 5px 40px;
        // margin-top: 0;
        text-align: center;
    }
`;

const Loading = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100vw;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(0, 0, 0, 0.5);
    z-index: 2000;
`;

const RequiredTag = styled.span`
    color: red;
`;

const InfoContainer = styled.div`
    position: relative;
    display: inline-block;
    cursor: pointer;

    &:hover .info-description {
        display: block;
    }
`;

export default function Kyc() {
    const navigate = useNavigate();
    const { claimsData, updateClaimsData, clearClaimsData } = DataContext();
    const [agreed, setAgreed] = useState(false);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [formDataError, setFormDataError] = useState(null);
    const [fundsOptions, setFundsOptions] = useState([]);
    const [businessNatureOptions, setBusinessNatureOptions] = useState([]);

    const _ClaimCreateService = new ClaimCreateService();

    const handleCheckboxChange = (e) => {
        setAgreed(e.target.checked);
    };

    const validateFormData = (formData) => {
        const fieldsToValidate = [
            { field: 'nationality', message: 'Nationality Required.' },
            { field: 'dob', message: 'Birthdate is Required.', pastDateMessage: 'Birthdate is invalid.' },
            { field: 'birthCity', message: 'City of Birth is Required.' },
            { field: 'tin', message: 'TIN number is required and must be 14 digits.', regex: /^\d{14}$/ },
            { field: 'gender', message: 'Gender is Required.' },
            { field: 'occupationClass', message: 'Nature of Work is Required.' },
            { field: 'empBusName', message: 'Name of Employer is Required.' },
            { field: 'empBusNature', message: 'Nature of Self-Employment/Business is Required.' },
            { field: 'sourceFund', message: 'Source of Funds is Required.' }
        ];

        let errors = [];

        fieldsToValidate.forEach(({ field, message, regex, pastDateMessage }) => {
            if (field === 'dob') {
                const dateOfBirth = new Date(formData[field]);
                const today = new Date();

                if (!formData[field]) {
                    errors.push({ item: field, message: message });
                } else if (dateOfBirth >= today) {
                    errors.push({ item: field, message: pastDateMessage });
                }
            } else if (field === 'tin') {
                // Remove dashes for validation
                const tinValue = formData[field]?.replace(/-/g, ''); // Remove dashes for validation

                if (!tinValue || (regex && !regex.test(tinValue))) {
                    errors.push({ item: field, message });
                }
            } else if (!formData[field] || (regex && !regex.test(formData[field]))) {
                errors.push({ item: field, message });
            }
        });

        return {
            isValid: errors.length === 0,
            errors
        };
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'tin') {
            // Remove all non-numeric characters
            let numericValue = value.replace(/\D/g, '');

            // Allow backspace when deleting values
            if (e.nativeEvent.inputType === 'deleteContentBackward') {
                setFormData((prevState) => ({
                    ...prevState,
                    [name]: numericValue // Just update without formatting
                }));
                return;
            }

            // Format the value to show 'xxx-xxx-xxx-xxx-xx'
            let formattedValue = '';

            if (numericValue.length <= 9) {
                // For the first 9 digits, format as 'xxx-xxx-xxx'
                formattedValue = numericValue.replace(/(\d{3})(\d{3})(\d{3})?/, '$1-$2-$3');
            } else if (numericValue.length <= 12) {
                // For 10-12 digits, format as 'xxx-xxx-xxx-xxx'
                formattedValue = numericValue.replace(/(\d{3})(\d{3})(\d{3})(\d{3})?/, '$1-$2-$3-$4');
            } else {
                // For 13-14 digits, format as 'xxx-xxx-xxx-xxx-xx'
                formattedValue = numericValue.replace(/(\d{3})(\d{3})(\d{3})(\d{3})(\d{2})/, '$1-$2-$3-$4-$5');
            }

            // Limit the formatted value to 19 characters (including dashes)
            formattedValue = formattedValue.slice(0, 18);

            // Update the state
            setFormData((prevState) => ({
                ...prevState,
                [name]: formattedValue
            }));
        } else {
            // Handle other fields
            setFormData((prevState) => ({
                ...prevState,
                [name]: value
            }));
        }
    };

    const handleSubmit = (e) => {
        try {
            e.preventDefault();
            setFormDataError(null); // Clear any previous errors

            const { isValid, errors } = validateFormData(formData);

            if (!agreed) {
                Swal.fire({
                    text: 'You must agree to the terms and conditions to proceed.',
                    icon: 'error',
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Ok',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    }
                });
                return;
            }

            // Remove dashes before submission
            const formattedTIN = formData?.tin?.replace(/-/g, ''); // Remove dashes from the TIN

            setFormData({ ...formData, tin: formattedTIN, formSubmitted: true });
            if (!isValid) {
                setFormDataError(errors);
                console.log(errors);
                return;
            }

            modalOptToUploadComponent(handleClientSelection);
        } catch (error) {
            console.log('error occured: ', error);
            Swal.fire({
                text: 'Something went wrong.',
                icon: 'error',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Ok',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            });
        }
    };

    const handleClientSelection = async (selected) => {
        const formattedTin = formData.tin.replace(/-/g, '');
        const middleName = claimsData?.claimsPayload?.customerDetails?.middleName;
        const name = `${claimsData.claimsPayload.customerDetails.firstName}${
            middleName ? ' ' + middleName + ' ' : ' '
        }${claimsData.claimsPayload.customerDetails.lastName}`;
        const claimsPayload = {
            ...claimsData.claimsPayload,
            customerDetails: {
                ...claimsData.claimsPayload.customerDetails,
                ...formData,
                fullName: name,
                displayName: name,
                tin: formattedTin
            }
        };

        updateClaimsData({
            ...claimsData,
            claimsPayload,
            kycFilledUp: true
        });

        console.log('claimsPayload', claimsPayload);
        MySwal.close();
        if (selected === 'upload_documents') {
            navigate('/documents');
            return;
        }

        setLoading(true);
        const response = await _ClaimCreateService.execute(claimsPayload);
        console.log(response);

        if (!response.claim) {
            Swal.fire({
                text: 'Sorry. Something went wrong. Please try again later.',
                icon: 'warning',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'OK',
                customClass: {
                    popup: 'my-popup',
                    confirmButton: 'my-confirm-button',
                    cancelButton: 'my-cancel-button'
                }
            });
            return;
        }

        setFormData({ ...formData, claimsSubmitSuccess: true });

        const refNo = response?.claim?.claimResult?.refNo ?? '00000000';

        modalSuccessClaimsCreateComponent(refNo, navigate, clearClaimsData);

        clearClaimsData();
        setLoading(false);
    };

    useEffect(() => {
        if (Object.keys(claimsData).length) {
            setFormData({ ...claimsData.claimsPayload.customerDetails, formSubmitted: false });
            updateClaimsData({ ...claimsData, kycFilledUp: false });
            if (!claimsData?.claimsPayload) {
                navigate('/home');
                clearClaimsData();
            }

            if (claimsData?.claimsPayload?.customerDetails.customerId) {
                Swal.fire({
                    text: 'KYC Finalized cannot be edited.',
                    icon: 'error',
                    iconColor: 'black',
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Ok',
                    customClass: {
                        popup: 'my-popup',
                        confirmButton: 'my-confirm-button',
                        cancelButton: 'my-cancel-button'
                    }
                });
                navigate('/home');
            }
        } else {
            navigate('/home');
        }
    }, []);

    useEffect(() => {
        if (formData?.formSubmitted) {
            const { isValid, errors } = validateFormData(formData);
            setFormDataError(errors);
        }
    }, [formData]);

    useEffect(() => {
        // Convert the mappings object to arrays of objects for easier use in dropdowns or similar
        setFundsOptions(Object.entries(sourceOfFunds).map(([key, value]) => ({ key, value })));

        setBusinessNatureOptions(Object.entries(employerBusinessNature).map(([key, value]) => ({ key, value })));
    }, []);

    return (
        <>
            {loading && (
                <Loading>
                    <Spinner animation="border" variant="light" />
                </Loading>
            )}
            {formData?.id && !formData?.claimsSubmitSuccess && (
                <StyledContainer fluid>
                    <Row className="pt-4">
                        <Title>File a Claim</Title>
                    </Row>
                    <Row className="pt-2">
                        <Description>
                            To complete your claim, please fill out the form with your basic information, source of
                            funds, and employer details.
                            <InfoContainer>
                                <i className="bi bi-info-circle" style={{ padding: '2px', fontSize: '18px' }}></i>
                                <StyledDescription className="info-description">
                                    Para makumpleto ang iyong claim, pakipunan ang form ng iyong pangunahing
                                    impormasyong, source of funds, at ang mga detalye ng iyong employer.
                                </StyledDescription>
                            </InfoContainer>
                        </Description>
                    </Row>
                    {/* <Row>
                        <StyledDescription>
                            Para makumpleto ang iyong claim, pakipunan ang form ng iyong pangunahing impormasyong,
                            source of funds, at ang mga detalye ng iyong employer.
                        </StyledDescription>
                    </Row> */}
                    <StyledForm onSubmit={handleSubmit}>
                        <Row>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        FIRST NAME<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={(e) => {
                                            if (!formData.firstName) {
                                                setFormData((val) => ({ ...val, firstName: e.target.value }));
                                            }
                                        }}
                                        disabled={formData?.firstName}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        LAST NAME<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={(e) => {
                                            if (!formData.lastName) {
                                                setFormData((val) => ({ ...val, lastName: e.target.value }));
                                            }
                                        }}
                                        disabled={formData?.lastName}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        NATIONALITY<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            name="nationality"
                                            value={formData.nationality}
                                            onChange={(e) =>
                                                setFormData((val) => ({ ...val, nationality: e.target.value }))
                                            }
                                            isInvalid={
                                                formDataError &&
                                                !!formDataError.find((item) => item.item === 'nationality')
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formDataError &&
                                                formDataError.find((item) => item.item === 'nationality')?.message}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        BIRTHDATE<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="date"
                                            name="dob"
                                            value={formData.dob}
                                            placeholder="mm/dd/yyyy"
                                            onChange={(e) => setFormData((val) => ({ ...val, dob: e.target.value }))}
                                            isInvalid={
                                                formDataError && !!formDataError.find((item) => item.item === 'dob')
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formDataError &&
                                                formDataError.find((item) => item.item === 'dob')?.message}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        CITY OF BIRTH<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            name="cob"
                                            value={formData.birthCity}
                                            onChange={(e) =>
                                                setFormData((val) => ({ ...val, birthCity: e.target.value }))
                                            }
                                            isInvalid={
                                                formDataError &&
                                                !!formDataError.find((item) => item.item === 'birthCity')
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formDataError &&
                                                formDataError.find((item) => item.item === 'birthCity')?.message}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        GENDER<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Select
                                            size="sm"
                                            name="gender"
                                            value={formData.gender}
                                            onChange={(e) => setFormData((val) => ({ ...val, gender: e.target.value }))}
                                            isInvalid={
                                                formDataError && !!formDataError.find((item) => item.item === 'gender')
                                            }
                                        >
                                            <option value="">Choose gender</option>
                                            <option value="m">Male</option>
                                            <option value="f">Female</option>
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {formDataError &&
                                                formDataError.find((item) => item.item === 'gender')?.message}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        EMAIL<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={(e) => {
                                            if (!formData.email) {
                                                setFormData((val) => ({ ...val, email: e.target.value }));
                                            }
                                        }}
                                        disabled={formData?.email}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        MOBILE NUMBER<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="mobileNo"
                                        value={formData.mobileNo}
                                        onChange={(e) => {
                                            if (!formData.mobileNo) {
                                                setFormData((val) => ({ ...val, mobileNo: e.target.value }));
                                            }
                                        }}
                                        disabled={formData?.mobileNo}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        TIN<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            name="tin"
                                            value={formData.tin}
                                            onChange={handleChange}
                                            isInvalid={
                                                formDataError && !!formDataError.find((item) => item.item === 'tin')
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formDataError &&
                                                formDataError.find((item) => item.item === 'tin')?.message}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={7}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        HOUSE NUMBER & STREET NAME<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="hnsn"
                                        value={formData.street}
                                        onChange={(e) => {
                                            if (!formData.street) {
                                                setFormData((val) => ({ ...val, street: e.target.value }));
                                            }
                                        }}
                                        disabled={formData?.street}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={5}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        BARANGAY<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="barangay"
                                        value={formData.barangay}
                                        onChange={(e) => {
                                            if (!formData.barangay) {
                                                setFormData((val) => ({ ...val, barangay: e.target.value }));
                                            }
                                        }}
                                        disabled={formData?.barangay}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={5}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        MUNICIPALITY/CITY<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="city"
                                        value={formData.city}
                                        onChange={(e) => {
                                            if (!formData.city) {
                                                setFormData((val) => ({ ...val, city: e.target.value }));
                                            }
                                        }}
                                        disabled={formData?.city}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={5}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        PROVINCE<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="province"
                                        value={formData.province}
                                        onChange={(e) => {
                                            if (!formData.province) {
                                                setFormData((val) => ({ ...val, province: e.target.value }));
                                            }
                                        }}
                                        disabled={formData?.province}
                                    />
                                </Form.Group>
                            </Col>
                            <Col lg={2}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        ZIP CODE<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="zipCode"
                                        value={formData.zipCode}
                                        onChange={(e) => {
                                            if (!formData.zipCode) {
                                                setFormData((val) => ({ ...val, zipCode: e.target.value }));
                                            }
                                        }}
                                        disabled={formData?.zipCode}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        NATURE OF WORK<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <Select
                                        name="occupationClass"
                                        id="occupationClass"
                                        options={businessNatureOptions}
                                        getOptionLabel={(option) => option.value}
                                        getOptionValue={(option) => option.key}
                                        value={businessNatureOptions.find(
                                            (option) => option.value === formData.occupationClass
                                        )}
                                        onChange={(selectedOption) =>
                                            setFormData((val) => ({ ...val, occupationClass: selectedOption.value }))
                                        }
                                        className={
                                            formDataError &&
                                            formDataError.find((item) => item.item === 'occupationClass')
                                                ? 'is-invalid'
                                                : ''
                                        }
                                    />
                                    {formDataError && formDataError.find((item) => item.item === 'occupationClass') && (
                                        <div className="invalid-feedback">
                                            {formDataError.find((item) => item.item === 'occupationClass')?.message}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        NAME OF EMPLOYER<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            name="noe"
                                            value={formData.empBusName}
                                            onChange={(e) =>
                                                setFormData((val) => ({ ...val, empBusName: e.target.value }))
                                            }
                                            isInvalid={
                                                formDataError &&
                                                !!formDataError.find((item) => item.item === 'empBusName')
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formDataError &&
                                                formDataError.find((item) => item.item === 'empBusName')?.message}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        NATURE OF SELF-EMPLOYMENT/BUSINESS<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            name="noseb"
                                            value={formData.empBusNature}
                                            onChange={(e) =>
                                                setFormData((val) => ({ ...val, empBusNature: e.target.value }))
                                            }
                                            isInvalid={
                                                formDataError &&
                                                !!formDataError.find((item) => item.item === 'empBusNature')
                                            }
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {formDataError &&
                                                formDataError.find((item) => item.item === 'empBusNature')?.message}
                                        </Form.Control.Feedback>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="form-group-label">
                                        SOURCE OF FUNDS<RequiredTag> *</RequiredTag>
                                    </Form.Label>
                                    <Select
                                        name="sourceFund"
                                        options={fundsOptions}
                                        getOptionLabel={(option) => option.value}
                                        getOptionValue={(option) => option.key}
                                        value={fundsOptions.find((option) => option.value === formData.sourceFund)}
                                        onChange={(selectedOption) =>
                                            setFormData((val) => ({ ...val, sourceFund: selectedOption.value }))
                                        }
                                        className={
                                            formDataError && formDataError.find((item) => item.item === 'sourceFund')
                                                ? 'is-invalid'
                                                : ''
                                        }
                                    />
                                    {formDataError && formDataError.find((item) => item.item === 'sourceFund') && (
                                        <div className="invalid-feedback">
                                            {formDataError.find((item) => item.item === 'sourceFund')?.message}
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <StyledContainer2>
                            <Col>
                                <Form.Group xs={12} controlId="formCheckbox" style={{ color: 'rgba(41, 37, 97, 1)' }}>
                                    <Form.Check
                                        className="checkbox mt-2"
                                        type="checkbox"
                                        onChange={handleCheckboxChange}
                                        name="agreed"
                                        label={
                                            <p>
                                                I have read, understood and agree to the{' '}
                                                <u>
                                                    <a
                                                        href="https://bpims.com/wp-content/uploads/2023/10/BPIMS_Stakeholder_Charter.pdf"
                                                        target="bpims"
                                                        style={{ fontWeight: '600', color: '#292561' }}
                                                    >
                                                        BPI MS Customer Charter
                                                    </a>
                                                </u>
                                                , the{' '}
                                                <u>
                                                    <a
                                                        href="https://bpims.com/wp-content/uploads/2024/08/Terms-of-Use_Home-Credit-Home-Insurance_Claims-Portal-1.pdf"
                                                        target="bpims"
                                                        style={{ fontWeight: '600', color: '#292561' }}
                                                    >
                                                        Terms of Use
                                                    </a>
                                                </u>{' '}
                                                of this microsite, and the{' '}
                                                <u>
                                                    <a
                                                        href="https://bit.ly/HomeProtectionTC"
                                                        target="bpims"
                                                        style={{ fontWeight: '600', color: '#292561' }}
                                                    >
                                                        Terms and Conditions
                                                    </a>
                                                </u>{' '}
                                                of my insurance plan, issued and underwritten by BPI/MS Insurance
                                                Corporation.
                                            </p>
                                        }
                                    />
                                </Form.Group>
                            </Col>
                        </StyledContainer2>
                        <Row>
                            <StyledSubmitButton
                                className="btn-submit me-2"
                                variant="secondary"
                                type="button"
                                onClick={() => {
                                    navigate('/details');
                                }}
                            >
                                PREVIOUS
                            </StyledSubmitButton>

                            <StyledSubmitButton2 variant="secondary" type="submit">
                                NEXT
                            </StyledSubmitButton2>
                        </Row>
                    </StyledForm>
                </StyledContainer>
            )}
        </>
    );
}
